/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/AdminUsers.tsx
 * DESCRIPTION: Admin dashboard for managing individual users who sign up
 *              directly to the SaaS (not through organizations). Provides
 *              user overview, subscription management, usage analytics,
 *              and user actions. Admin-only access with Clerk integration.
 *
 * FEATURES:
 * - Admin-only access with authentication
 * - User list with search and filters
 * - User details with usage stats
 * - Subscription level management
 * - User activity tracking
 * - Dark mode support throughout
 * - Fully functional with mock data
 * - Ready for Clerk user management API
 *
 * DATA INTEGRATION:
 * - Mock data structured for easy Clerk API replacement
 * - All filters and search functional
 * - User actions prepared for backend integration
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-03-27
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Shield,
  TrendingUp,
  DollarSign,
  Activity,
  Search,
  Filter,
  MoreVertical,
  Mail,
  Lock,
  Unlock,
  Trash2,
  Edit,
  Check,
  X,
  Eye,
  EyeOff,
  Download,
  Calendar,
  Clock,
  CreditCard,
  AlertCircle,
  CheckCircle,
  LogOut,
  LockKeyhole,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────
interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  plan: 'free' | 'pro' | 'enterprise';
  status: 'active' | 'suspended' | 'pending';
  signedUpDate: string;
  lastActive: string;
  usage: {
    filesAnalyzed: number;
    apiCalls: number;
    storageUsed: number;
  };
  subscription: {
    status: 'active' | 'canceled' | 'expired';
    currentPeriodEnd: string;
    amount: number;
  };
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  mrr: number;
  churnRate: number;
}

type SortField = 'email' | 'plan' | 'status' | 'signedUpDate' | 'lastActive';
type SortOrder = 'asc' | 'desc';

// ─────────────────────────────────────────────────────────────────────
// MOCK DATA - Replace with Clerk API calls
// ─────────────────────────────────────────────────────────────────────
const MOCK_USER_STATS: UserStats = {
  totalUsers: 1247,
  activeUsers: 892,
  mrr: 12450,
  churnRate: 2.3,
};

const MOCK_USERS: AdminUser[] = [
  {
    id: '1',
    email: 'john.doe@example.com',
    firstName: 'John',
    lastName: 'Doe',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
    plan: 'pro',
    status: 'active',
    signedUpDate: '2026-01-15',
    lastActive: '2026-03-27 14:30',
    usage: {
      filesAnalyzed: 185,
      apiCalls: 73450,
      storageUsed: 28.5,
    },
    subscription: {
      status: 'active',
      currentPeriodEnd: '2026-04-15',
      amount: 2900,
    },
  },
  {
    id: '2',
    email: 'sarah.chen@company.com',
    firstName: 'Sarah',
    lastName: 'Chen',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
    plan: 'enterprise',
    status: 'active',
    signedUpDate: '2026-02-20',
    lastActive: '2026-03-27 16:45',
    usage: {
      filesAnalyzed: 1247,
      apiCalls: 456000,
      storageUsed: 156.8,
    },
    subscription: {
      status: 'active',
      currentPeriodEnd: '2026-04-20',
      amount: 9900,
    },
  },
  {
    id: '3',
    email: 'mike.johnson@startup.io',
    firstName: 'Mike',
    lastName: 'Johnson',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
    plan: 'free',
    status: 'active',
    signedUpDate: '2026-03-10',
    lastActive: '2026-03-26 09:15',
    usage: {
      filesAnalyzed: 45,
      apiCalls: 8500,
      storageUsed: 5.2,
    },
    subscription: {
      status: 'active',
      currentPeriodEnd: '2026-04-10',
      amount: 0,
    },
  },
  {
    id: '4',
    email: 'emma.wilson@design.co',
    firstName: 'Emma',
    lastName: 'Wilson',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
    plan: 'pro',
    status: 'suspended',
    signedUpDate: '2026-01-25',
    lastActive: '2026-03-20 11:30',
    usage: {
      filesAnalyzed: 320,
      apiCalls: 95000,
      storageUsed: 42.3,
    },
    subscription: {
      status: 'canceled',
      currentPeriodEnd: '2026-03-25',
      amount: 2900,
    },
  },
  {
    id: '5',
    email: 'alex.brown@tech.com',
    firstName: 'Alex',
    lastName: 'Brown',
    avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100',
    plan: 'pro',
    status: 'active',
    signedUpDate: '2026-02-05',
    lastActive: '2026-03-27 10:00',
    usage: {
      filesAnalyzed: 567,
      apiCalls: 182000,
      storageUsed: 78.9,
    },
    subscription: {
      status: 'active',
      currentPeriodEnd: '2026-04-05',
      amount: 2900,
    },
  },
  {
    id: '6',
    email: 'lisa.martinez@media.net',
    firstName: 'Lisa',
    lastName: 'Martinez',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
    plan: 'free',
    status: 'pending',
    signedUpDate: '2026-03-25',
    lastActive: '2026-03-25 15:20',
    usage: {
      filesAnalyzed: 12,
      apiCalls: 1200,
      storageUsed: 1.8,
    },
    subscription: {
      status: 'active',
      currentPeriodEnd: '2026-04-25',
      amount: 0,
    },
  },
];

const MOCK_USAGE_TREND = [
  { date: '2026-03-21', files: 1250, api: 425000 },
  { date: '2026-03-22', files: 1380, api: 468000 },
  { date: '2026-03-23', files: 1150, api: 392000 },
  { date: '2026-03-24', files: 1520, api: 518000 },
  { date: '2026-03-25', files: 1680, api: 572000 },
  { date: '2026-03-26', files: 1420, api: 485000 },
  { date: '2026-03-27', files: 1890, api: 645000 },
];

const MOCK_PLAN_DISTRIBUTION = [
  { name: 'Free', value: 65, color: '#6b7280' },
  { name: 'Pro', value: 28, color: '#3b82f6' },
  { name: 'Enterprise', value: 7, color: '#8b5cf6' },
];

const ADMIN_CREDENTIALS = {
  email: 'admin@verimark.ai',
  password: 'admin2026',
};

// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount / 100);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getPlanBadge = (plan: AdminUser['plan']) => {
  const badges = {
    free: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
    pro: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    enterprise: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  };
  return badges[plan];
};

const getStatusBadge = (status: AdminUser['status']) => {
  const badges = {
    active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  };
  return badges[status];
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const AdminUsers: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('signedUpDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  // ───────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoaded) {
      checkAdminAccess();
    }
  }, [isLoaded, user]);

  useEffect(() => {
    const loadData = async () => {
      if (isAdmin) {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        setUserStats(MOCK_USER_STATS);
        setUsers(MOCK_USERS);
        setIsLoading(false);
      }
    };
    loadData();
  }, [isAdmin]);

  // ───────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────
  const checkAdminAccess = () => {
    const userMetadata = user?.publicMetadata as { role?: string } | undefined;
    if (userMetadata?.role === 'admin') {
      setIsAdmin(true);
      setShowLoginModal(false);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (adminEmail === ADMIN_CREDENTIALS.email && adminPassword === ADMIN_CREDENTIALS.password) {
      setIsAdmin(true);
      setShowLoginModal(false);
      setAdminEmail('');
      setAdminPassword('');
    } else {
      setLoginError('Invalid admin credentials');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    setShowLoginModal(true);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleSuspendUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, status: u.status === 'active' ? 'suspended' : 'active' } : u
      ));
    } catch (error) {
      console.error('Failed to update user status:', error);
      alert('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user');
    }
  };

  const handleSignOut = () => {
    signOut(() => {
      window.location.href = '/sign-in';
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // FILTERED & SORTED USERS
  // ───────────────────────────────────────────────────────────────────
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.lastName.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesPlan = planFilter === 'all' || user.plan === planFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    return matchesSearch && matchesPlan && matchesStatus;
  }).sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'email':
        comparison = a.email.localeCompare(b.email);
        break;
      case 'plan':
        comparison = a.plan.localeCompare(b.plan);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'signedUpDate':
        comparison = new Date(a.signedUpDate).getTime() - new Date(b.signedUpDate).getTime();
        break;
      case 'lastActive':
        comparison = new Date(a.lastActive.replace(' ', 'T')).getTime() - new Date(b.lastActive.replace(' ', 'T')).getTime();
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Admin Login Modal
  // ───────────────────────────────────────────────────────────────────
  if (!isAdmin && showLoginModal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Admin Access Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              This page contains sensitive user data. Please authenticate as an administrator.
            </p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Admin Email
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="admin@verimark.ai"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {loginError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{loginError}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Lock className="w-5 h-5" />
              Authenticate
            </button>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Back to Dashboard
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>Demo Credentials:</strong><br />
              Email: admin<br />
              Password: ********* 
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Main Admin Users Page
  // ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                User Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage individual users, subscriptions, and usage analytics
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg">
                <Shield className="w-5 h-5" />
                <span className="text-sm font-medium">Admin Access</span>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Exit Admin</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards - Dashboard Style */}
        {userStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {userStats.totalUsers.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>+12% this month</span>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <Activity className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Active Users</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {userStats.activeUsers.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {((userStats.activeUsers / userStats.totalUsers) * 100).toFixed(1)}% activation rate
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(userStats.mrr * 100)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>+8% vs last month</span>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Churn Rate</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {userStats.churnRate}%
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>-0.5% improvement</span>
              </div>
            </div>
          </div>
        )}

        {/* Filters & Search */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[300px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email, name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Plans</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
              <Download className="w-5 h-5" />
              Export Users
            </button>
          </div>
        </div>

        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - User List */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  All Users ({filteredUsers.length})
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('email')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          User
                          {sortField === 'email' && (sortOrder === 'asc' ? <Check className="w-4 h-4" /> : <Check className="w-4 h-4 rotate-180" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('plan')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          Plan
                          {sortField === 'plan' && (sortOrder === 'asc' ? <Check className="w-4 h-4" /> : <Check className="w-4 h-4 rotate-180" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('status')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          Status
                          {sortField === 'status' && (sortOrder === 'asc' ? <Check className="w-4 h-4" /> : <Check className="w-4 h-4 rotate-180" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('signedUpDate')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          Signed Up
                          {sortField === 'signedUpDate' && (sortOrder === 'asc' ? <Check className="w-4 h-4" /> : <Check className="w-4 h-4 rotate-180" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('lastActive')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          Last Active
                          {sortField === 'lastActive' && (sortOrder === 'asc' ? <Check className="w-4 h-4" /> : <Check className="w-4 h-4 rotate-180" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-600 dark:text-gray-400">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {user.avatar ? (
                              <img
                                src={user.avatar}
                                alt={user.firstName}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <span className="text-blue-600 dark:text-blue-400 font-medium">
                                  {user.firstName.charAt(0)}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getPlanBadge(user.plan)}`}>
                            {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(user.status)}`}>
                            {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(user.signedUpDate)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {user.lastActive}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowUserDetails(true);
                              }}
                              className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleSuspendUser(user.id)}
                              className="p-2 text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                              title={user.status === 'active' ? 'Suspend' : 'Activate'}
                            >
                              {user.status === 'active' ? (
                                <Lock className="w-4 h-4" />
                              ) : (
                                <Unlock className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column - Analytics */}
          <div className="space-y-6">
            {/* Usage Trend */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Usage Trend (7 Days)
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={MOCK_USAGE_TREND}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" fontSize={10} tickFormatter={(val) => val.slice(5)} />
                  <YAxis stroke="#6b7280" fontSize={10} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="files" stroke="#3b82f6" strokeWidth={2} dot={false} name="Files" />
                  <Line type="monotone" dataKey="api" stroke="#8b5cf6" strokeWidth={2} dot={false} name="API Calls" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Plan Distribution */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Plan Distribution
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={MOCK_PLAN_DISTRIBUTION}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {MOCK_PLAN_DISTRIBUTION.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <button className="w-full flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg font-medium transition-colors">
                  <Mail className="w-5 h-5" />
                  Send Newsletter
                </button>
                <button className="w-full flex items-center gap-3 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-lg font-medium transition-colors">
                  <CreditCard className="w-5 h-5" />
                  View Revenue Report
                </button>
                <button className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors">
                  <Calendar className="w-5 h-5" />
                  Schedule Maintenance
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowUserDetails(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">User Details</h3>
              <button
                onClick={() => setShowUserDetails(false)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-4">
                {selectedUser.avatar ? (
                  <img
                    src={selectedUser.avatar}
                    alt={selectedUser.firstName}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <span className="text-2xl text-blue-600 dark:text-blue-400 font-medium">
                      {selectedUser.firstName.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">{selectedUser.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getPlanBadge(selectedUser.plan)}`}>
                      {selectedUser.plan.charAt(0).toUpperCase() + selectedUser.plan.slice(1)}
                    </span>
                    <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(selectedUser.status)}`}>
                      {selectedUser.status.charAt(0).toUpperCase() + selectedUser.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Usage Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Files Analyzed</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedUser.usage.filesAnalyzed.toLocaleString()}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">API Calls</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedUser.usage.apiCalls.toLocaleString()}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Storage (GB)</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedUser.usage.storageUsed}
                  </p>
                </div>
              </div>

              {/* Subscription Info */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 dark:text-white mb-3">Subscription</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                    <p className="font-medium text-gray-900 dark:text-white capitalize">
                      {selectedUser.subscription.status}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Amount</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(selectedUser.subscription.amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Signed Up</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDate(selectedUser.signedUpDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Renews On</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDate(selectedUser.subscription.currentPeriodEnd)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
                  <Edit className="w-5 h-5" />
                  Edit User
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors">
                  <Mail className="w-5 h-5" />
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
